import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ConfirmComponent } from '@app/core/info/confirm/confirm.component';
import { MatTableHelper } from '@app/shared/helpers/table-filter.helper';
import { TranslateService } from '@ngx-translate/core';
import { BusinessUserComponent } from '../business-user/business-user.component';
import { IConfirmData } from '@app/core/info/confirm/confirm';
import { MatPaginator } from '@angular/material/paginator';
import { BusinessService } from '../business.service';
import { SessionStorageService } from '@app/core';
import { InfoService } from '@app/core/info/info.service';
import { IBusinessGroup, IBusinessUser, IChangeStatusRequest, UserStatus, rolesDictionary } from '../business-models';

@Component({
  selector: 'cloud-business-users-table',
  templateUrl: './business-users-table.component.html',
  styleUrls: ['./business-users-table.component.scss']
})
export class BusinessUsersTableComponent implements OnInit, OnChanges {

  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() tableName: string;
  @Input() group: IBusinessGroup;
  @Input() inputValue: any;

  @Output() check: EventEmitter<any> = new EventEmitter();

  tableData: IBusinessUser[];
  dataSource = new MatTableDataSource();
  usersColumns: string[];

  private confirmDialog: MatDialogRef<ConfirmComponent>;
  private pageDialog: MatDialogRef<BusinessUserComponent>;

  nameFilter = new FormControl();
  positionFilter = new FormControl();
  emailFilter = new FormControl();
  companyId: string;
  searched = false;

  filteredValues = {
    position: '',
    name: '',
    email: ''
  };

  UserStatus = UserStatus;
  pageDialogSize = 5;
  pageDialogOptions = [5];

  constructor(
    public tableHelper: MatTableHelper,
    public translateService: TranslateService,
    private dialog: MatDialog,
    private businessService: BusinessService,
    private sessionStorageService: SessionStorageService,
    public info: InfoService

  ) { }


  ngOnInit() {
    this.getCurrentLoggedInAccount();

    this.nameFilter.valueChanges.subscribe((nameFilterValue) => {
      this.filteredValues['name'] = nameFilterValue;
      if (this.checkFilterDigits(nameFilterValue)) {
        this.filter();
      }
    });

    this.positionFilter.valueChanges.subscribe((positionFilterValue) => {
      this.filteredValues['position'] = positionFilterValue;
      if (this.checkFilterDigits(positionFilterValue)) {
        this.filter();
      }
    });

    this.emailFilter.valueChanges.subscribe((emailFilterValue) => {
      this.filteredValues['email'] = emailFilterValue;
      if (this.checkFilterDigits(emailFilterValue)) {
        this.filter();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    
    // Apply appropriate filtering based on tableName
    if (this.tableName === 'groups' && this.group) {
      // For 'groups' table in the expansion panel, filter users belonging to the specified group
      this.tableData = this.inputValue.filter(p => p.groups.find(g => g.id === this.group.id));
    } else {
      // For user table
      this.tableData = this.inputValue;
    }
    
    // Update data source
    this.dataSource = new MatTableDataSource(this.tableData);
    this.dataSource.paginator = this.paginator;

    // Set appropriate columns
    if (this.tableName === 'inGroupDialog') {
      this.usersColumns = ['name', 'role', 'position', 'email', 'phone'];
    } else {
      this.usersColumns = ['name', 'role', 'position', 'email', 'phone', 'actions'];
    }
  }

  getCurrentLoggedInAccount() {
    this.companyId = this.sessionStorageService.getItem("businessAccountId");
  }

  checkFilterDigits(f) {
    return (!this.searched && (f.length != 1)) || (this.searched && (f.length != 1 && f.length != 2))
  }

  filter() {
    this.searched = true;
    this.dataSource.filter = JSON.stringify(this.filteredValues);
    this.dataSource.filterPredicate = this.customFilterPredicate();
  }

  customFilterPredicate() {
    const filterPredicate = (data: any, filter: string): boolean => {
      let searchString = JSON.parse(filter);
      return data.position.toString().trim().toLowerCase().indexOf(searchString.position.toLowerCase()) !== -1 &&
        (data.fullNameEn.toString().trim().toLowerCase().indexOf(searchString.name.toLowerCase()) !== -1 || data.fullNameBg.toString().trim().toLowerCase().indexOf(searchString.name.toLowerCase()) !== -1) &&
        data.email.toString().trim().toLowerCase().indexOf(searchString.email.toLowerCase()) !== -1;
    }
    return filterPredicate;
  }


  getName(e) {
    return this.lang == 'bg' ? e.fullNameBg : e.fullNameEn;
  }

  get lang() {
    return this.translateService.currentLang;
  }

  getRole(key) {
    return this.translateService.instant('businessTexts.' + rolesDictionary[key]);
  }

  addUser() {
    this.openUserDialog('add');
  }

  onDeleteUser(user) {
    let isSingleAdmin = false;
    if (this.isAdmin(user)) {
      isSingleAdmin = this.checkSingleAdmin();
    }
    this.openDeleteUserConfirmDialog(user, isSingleAdmin);
  }

  onUnblockUser(user) {
    this.openUnblockConfirmDialog(user);
  }

  onBlockUser(user) {
    let isSingleAdmin = false;
    if (this.isAdmin(user)) {
      isSingleAdmin = this.checkSingleAdmin();
    }
    this.openBlockConfirmDialog(user, isSingleAdmin);
  }

  checkSingleAdmin() {
    return this.tableData.filter(u => this.isAdmin(u)).length == 1;
  }

  isAdmin(user: IBusinessUser) {
    return user.role === 'MY_BTRUST_ADMINISTRATOR' || user.role === 'MY_BTRUST_SUPER_ADMINISTRATOR';
  }

  onEditUser(user) {
    this.openUserDialog('edit', user);
  }

  removeFromGroup(user) {
    this.openRemoveConfirmDialog(user, this.group);
  }

  openDeleteUserConfirmDialog(user, isSingleAdmin) {
    this.confirmDialog = this.dialog.open(
      ConfirmComponent,
      {
        data: {
          title: this.translateService.instant('businessTexts.dialogTexts.deleteUserTitle'),
          question: isSingleAdmin ? this.translateService.instant('businessTexts.dialogTexts.deleteSingleAdminQuestion', { user: this.getName(user), role: this.getRole(user.role) }) : this.translateService.instant('businessTexts.dialogTexts.deleteUserQuestion', { user: this.getName(user), role: this.getRole(user.role) }),
          confirmTxt: this.translateService.instant('settingsList.common.confirm'),
          cancelTxt: this.translateService.instant('settingsList.common.cancel'),
          confirmAction: () => this.deleteUserFunction(user)
        } as IConfirmData
      });
  }

  openBlockConfirmDialog(user, isSingleAdmin) {
    this.confirmDialog = this.dialog.open(
      ConfirmComponent,
      {
        data: {
          title: this.translateService.instant('businessTexts.dialogTexts.blockUserTitle'),
          question: isSingleAdmin ? this.translateService.instant('businessTexts.dialogTexts.blockSingleAdminQuestion', { user: this.getName(user), role: this.getRole(user.role) }) : this.translateService.instant('businessTexts.dialogTexts.blockUserQuestion', { user: this.getName(user), role: this.getRole(user.role) }),
          confirmTxt: this.translateService.instant('settingsList.common.confirm'),
          cancelTxt: this.translateService.instant('settingsList.common.cancel'),
          confirmAction: () => this.blockUserFunction(user)
        } as IConfirmData
      });
  }

  openUnblockConfirmDialog(user) {
    this.confirmDialog = this.dialog.open(
      ConfirmComponent,
      {
        data: {
          title: this.translateService.instant('businessTexts.dialogTexts.unblockUserTitle'),
          question: this.translateService.instant('businessTexts.dialogTexts.unblockUserQuestion', { user: this.getName(user), role: this.getRole(user.role) }),
          confirmTxt: this.translateService.instant('settingsList.common.confirm'),
          cancelTxt: this.translateService.instant('settingsList.common.cancel'),
          confirmAction: () => this.unblockUserFunction(user)
        } as IConfirmData
      });
  }

  openRemoveConfirmDialog(user, group) {
    this.confirmDialog = this.dialog.open(
      ConfirmComponent,
      {
        data: {
          title: this.translateService.instant('businessTexts.dialogTexts.removeUserTitle', { group: group.name }),
          question: this.translateService.instant('businessTexts.dialogTexts.removeUserQuestion', { user: this.getName(user), group: group.name }),
          confirmTxt: this.translateService.instant('settingsList.common.confirm'),
          cancelTxt: this.translateService.instant('settingsList.common.cancel'),
          confirmAction: () => this.removeUserFromGroupFunction(user, group)
        } as IConfirmData
      });
  }


  deleteUserFunction(user) {
    this.businessService.deleteUser(user.customerUserId).subscribe(res => {
      if (res.status == 200) {
        this.info.success(this.translateService.instant('businessTexts.successDeleteUser'));
        this.businessService.updateUserTable.next(true);
      } else {
        this.info.error(this.translateService.instant('businessTexts.errorDeleteUser'));
      }
    })
    this.confirmDialog.close();
  }

  blockUserFunction(user) {
    let changeStatusRequest: IChangeStatusRequest = { customerUserId: user.customerUserId, status: 'BLOCKED' }
    this.businessService.changeUserStatus(changeStatusRequest).subscribe(res => {
      if (res.status == 200) {
        this.info.success(this.translateService.instant('businessTexts.successBlockUser'));
        this.businessService.updateUserTable.next(true);
      } else {
        this.info.error(this.translateService.instant('businessTexts.errorBlockUser'));
      }
    })
    this.confirmDialog.close();
  }

  unblockUserFunction(user) {
    let changeStatusRequest: IChangeStatusRequest = { customerUserId: user.customerUserId, status: 'ACTIVE' }
    this.businessService.changeUserStatus(changeStatusRequest).subscribe(res => {
      if (res.status == 200) {
        this.info.success(this.translateService.instant('businessTexts.successUnblockUser'));
        this.businessService.updateUserTable.next(true);
      } else {
        this.info.error(this.translateService.instant('businessTexts.errorUnblockUser'));
      }
    })
    this.confirmDialog.close();
  }

  removeUserFromGroupFunction(user, group) {
    this.businessService.removeUserFromGroup(user.customerUserId, group.id).subscribe(res => {
      if (res.status == 200) {
        this.info.success(this.translateService.instant('businessTexts.successRemoveUser'));
        this.businessService.updateUserTable.next(true);
        this.businessService.updateNavBar.next(true);
      } else {
        this.info.error(this.translateService.instant('businessTexts.errorRemoveUser'));
      }
    })
    this.confirmDialog.close();
  }


  openUserDialog(action, user?) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    if (user) {
      dialogConfig.data = {
        action: action,
        user: user
      };
    } else {
      dialogConfig.data = {
        action: action,
        users: this.tableData
      };
    }

    this.pageDialog = this.dialog.open(BusinessUserComponent, dialogConfig);

    const sub = this.pageDialog.componentInstance.close.subscribe((close: boolean) => {
      if (close == false) {
        this.pageDialog.close();
      } else {
        this.pageDialog.close();
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  getColumnCount() {
    return this.usersColumns.length;
  }

  onCheckChange(checked, element) {
    this.check.emit({ checked, element });
  }

  isChecked(element) {
    if (this.group == undefined) {
      return false;
    } else {
      const found = element.groups.find((obj) => {
        return obj.id === this.group.id;
      });
      return found ? true : false;
    }
  }
}
